import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface SsdgWizardState {
  current: number;
  data: Record<string, unknown>;
  visite_id: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SsdgWizardService {
  private state: SsdgWizardState = {
    current: 0,
    data: {},
    visite_id: null,
  };

  /** True when the application wizard is the active route. */
  private readonly _wizardActive = signal(false);
  /** True when the wizard is in readonly mode (no edits). */
  private readonly _wizardReadOnly = signal(true);
  /** True when the current wizard step has unsaved local changes. */
  private readonly _hasUnsavedChanges = signal(false);
  /** True when the current application status is completed (used to show Status button in header). */
  private readonly _applicationStatusCompleted = signal(false);

  /** Show the header save button when the wizard is active and not readonly. */
  readonly showSaveInHeader = computed(
    () => this._wizardActive() && !this._wizardReadOnly()
  );

  /** True when the current application status is completed (e.g. completed, complete, terminated). */
  readonly applicationStatusIsCompleted = this._applicationStatusCompleted.asReadonly();

  /** Show a red unsaved indicator dot on the save icon. */
  readonly showUnsavedIndicator = computed(
    () => this.showSaveInHeader() && this._hasUnsavedChanges()
  );

  private readonly saveRequested = new Subject<void>();
  /** Emits when the header save button is clicked. Wizard should subscribe and call saveCurrentStepData(). */
  readonly onSaveRequested = this.saveRequested.asObservable();

  /** Call when the wizard component becomes active or when its readonly state changes. */
  setWizardContext(active: boolean, readOnly?: boolean): void {
    this._wizardActive.set(active);
    if (active && readOnly !== undefined) {
      this._wizardReadOnly.set(readOnly);
    }
    if (!active) {
      this._wizardReadOnly.set(true);
      this._hasUnsavedChanges.set(false);
      this._applicationStatusCompleted.set(false);
    }
  }

  /** Call when the wizard has resolved application status (completed vs in progress). */
  setApplicationStatusCompleted(completed: boolean): void {
    this._applicationStatusCompleted.set(completed);
  }

  /** Mark that the current step has unsaved changes. */
  markUnsavedChanges(): void {
    if (!this._wizardActive() || this._wizardReadOnly()) return;
    this._hasUnsavedChanges.set(true);
  }

  /** Mark that all changes are saved. */
  markSaved(): void {
    this._hasUnsavedChanges.set(false);
  }

  /** Called by the header when the user clicks Save. */
  requestSave(): void {
    this.saveRequested.next();
  }

  saveState(state: Partial<SsdgWizardState>): void {
    this.state = { ...this.state, ...state };
  }

  getState(): SsdgWizardState {
    return { ...this.state };
  }

  reset(): void {
    this.state = { current: 0, data: {}, visite_id: null };
  }
}
